import { getAllCustomers } from '@/redux/actions/customerActions';
import {
	assignMachineToCustomer,
	assignMachineToMultipleCustomers,
	getAllAssignedMachines,
	getUnassignedMachines,
} from '@/redux/actions/gpuActions';
import { Button } from '@/shared/components/Button';
import { Card, CardBody } from '@/shared/components/Card';
import SelectField from '@/shared/components/form/Fields/SelectField';
import {
	ModalCloseButton,
	ModalHeader,
	ModalTitle,
	StyledModal,
} from '@/shared/components/Modal';
import ReactDataTableComponent from '@/shared/components/ReactDataTableComponent';
import { TableSkeleton } from '@/shared/components/Skeleton/TableSkeleton';
import ToolTipButton from '@/shared/components/ToolTipButton';
import dayjs from 'dayjs';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useGpuOneFilter from '../hooks/useGpuOneFilter';
import GpuOneFilterSection from './GpuOneFilterSection';
const ViewDetailsModal = lazy(() => import('./GpuOneViewDetailsModal'));

const GpuOneUnAssignedMachinesTable = () => {
	const dispatch = useDispatch();
	const { unassignedGPUOne, isLoading } = useSelector(state => state.gpuOne);
	const { machines, total, currentPage, perPage, sortBy } = unassignedGPUOne;

	// Use the custom hook for filtering
	const {
		filters,
		apiFilters,
		handleFilterChange,
		handleSortChange,
		handleClearFilters,
		getApiFilters,
	} = useGpuOneFilter(machines);

	const [sortField, setSortField] = useState('last_updated');
	const [sortAsc, setSortAsc] = useState(false);
	const [assignCustomerServerModal, setAssignCustomerServerModal] =
		useState(false);
	const [rowData, setRowData] = useState(null);
	const [viewDetailsModal, setViewDetailsModal] = useState(false);
	const [selectedCustomer, setSelectedCustomer] = useState(null);
	const [selectedSystemSerialList, setSelectedSystemSerialList] = useState([]);
	const [selectedDeviceNames, setSelectedDeviceNames] = useState([]);
	const [selectedDeviceData, setSelectedDeviceData] = useState([]);
	const [showAllDevices, setShowAllDevices] = useState(false);
	const allCustomers = useSelector(state => state?.customer?.allCustomers);

	useEffect(() => {
		dispatch(getAllCustomers());
	}, []);

	const CustomerOptions = allCustomers?.map(option => ({
		value: option?.customerID,
		label: option?.customerName,
	}));

	const handleAssignMachine = async () => {
		if (!selectedCustomer || !rowData) return;

		try {
			const customerData = {
				customerID: selectedCustomer.value,
				customerName: selectedCustomer.label,
			};

			await dispatch(
				assignMachineToCustomer(rowData.system_serial, customerData),
			);
			setAssignCustomerServerModal(false);
			setSelectedCustomer(null);
			setRowData(null);
			// Refresh both unassigned and assigned machines data after assignment
			fetchData(1, 10, '-last_updated');
			dispatch(getAllAssignedMachines());
		} catch (error) {
			console.error('Error assigning machine:', error);
		}
	};

	const handleAssignMachineToMultiple = async () => {
		await dispatch(
			assignMachineToMultipleCustomers(
				selectedSystemSerialList,
				selectedCustomer.value,
			),
		);
		setAssignCustomerServerModal(false);
		setSelectedCustomer(null);
		setRowData(null);
		// Refresh both unassigned and assigned machines data after assignment
		fetchData(1, 10, '-last_updated');
		dispatch(getAllAssignedMachines());
	};

	const fetchData = async (page, numPerPage, sortByParam) => {
		const params = {
			page: page || currentPage,
			perPage: numPerPage || perPage,
			sortBy: sortByParam || apiFilters.sort || sortBy,
			filters: getApiFilters(),
		};

		dispatch(getUnassignedMachines(params));
	};

	const handleSort = (column, direction) => {
		setSortField(column?.id);
		setSortAsc(direction === 'asc');
		const sortByParam = column?.id
			.split(',')
			.map(x => (direction !== 'asc' ? `-${x}` : x))
			.join(',');

		fetchData(1, undefined, sortByParam);
	};

	const handlePageChange = async page => {
		fetchData(page);
	};

	const handlePerRowsChange = async (newPerPage, page) => {
		fetchData(page, newPerPage);
	};

	const assignCustomerPrefix = rowData => {
		setAssignCustomerServerModal(true);
		if (rowData) {
			setRowData(rowData);
		}
		setSelectedCustomer(null);
	};

	const viewDetails = rowData => {
		console.log(rowData);
		setViewDetailsModal(true);
		setRowData(rowData);
	};

	useEffect(() => {
		fetchData(1, 10, '-last_updated');
	}, []);

	const prevApiFiltersRef = useRef();

	// Re-fetch data when API filters change (not on every keystroke)
	// Always reset to page 1 when filters change to show all filtered results
	useEffect(() => {
		const apiFiltersString = JSON.stringify(apiFilters);
		const prevApiFiltersString = JSON.stringify(prevApiFiltersRef.current);

		if (apiFiltersString !== prevApiFiltersString) {
			prevApiFiltersRef.current = apiFilters;
			fetchData(1, perPage, apiFilters.sort || sortBy);
		}
	}, [apiFilters, perPage, sortBy]);

	const columns = [
		{
			id: 'machine_name',
			name: 'Machine Name',
			selector: row => row.machine_name,
			sortable: true,
			reorder: true,
			cell: row => (
				<div>
					<p
						style={{ cursor: 'pointer', textDecoration: 'underline' }}
						onClick={() => viewDetails(row)}
					>
						{row.machine_name}
					</p>
				</div>
			),
		},
		{
			id: 'maas_id',
			name: 'MAAS ID',
			selector: row => row.maas_id,
			sortable: true,
			reorder: true,
			cell: row => (
				<div>
					<p>{row.maas_id}</p>
				</div>
			),
		},
		{
			id: 'system_vendor',
			name: 'System Vendor',
			selector: row => row.system_vendor,
			sortable: true,
			reorder: true,
			width: '150px',
			cell: row => (
				<div>
					<p>{row.system_vendor}</p>
				</div>
			),
		},
		{
			id: 'system_product',
			name: 'System Product',
			selector: row => row.system_product,
			sortable: true,
			reorder: true,
			cell: row => (
				<div>
					<p>{row.system_product}</p>
				</div>
			),
		},
		{
			id: 'system_serial',
			name: 'System Serial',
			selector: row => row.system_serial,
			sortable: true,
			reorder: true,
			cell: row => (
				<div>
					<p>{row.system_serial}</p>
				</div>
			),
		},
		{
			id: 'total_cpu_cores',
			name: 'CPU Cores',
			selector: row => row.total_cpu_cores,
			sortable: true,
			reorder: true,
			cell: row => (
				<div>
					<p>{row.total_cpu_cores}</p>
				</div>
			),
		},
		{
			id: 'cpu_type',
			name: 'CPU Type',
			selector: row => row.cpu_type,
			sortable: true,
			reorder: true,
			cell: row => (
				<div>
					<p>{row.cpu_type}</p>
				</div>
			),
		},
		{
			id: 'total_memory',
			name: 'Memory (GB)',
			selector: row => row.total_memory,
			sortable: true,
			reorder: true,
			cell: row => (
				<div>
					<p>{row.total_memory}</p>
				</div>
			),
		},
		{
			id: 'machine_status',
			name: 'Status',
			selector: row => row.machine_status,
			sortable: true,
			reorder: true,
			cell: row => (
				<div>
					<p>{row.machine_status}</p>
				</div>
			),
		},
		{
			id: 'deployed_os',
			name: 'Deployed OS',
			selector: row => row.deployed_os,
			sortable: true,
			reorder: true,
			cell: row => (
				<div>
					<p>{row.deployed_os}</p>
				</div>
			),
		},
		{
			id: 'last_updated',
			name: 'Last Updated',
			selector: row => row.last_updated,
			sortable: true,
			reorder: true,
			cell: row => (
				<div>
					<p>
						{row.last_updated
							? dayjs(row.last_updated).format('MM/DD/YYYY hh:mm A')
							: 'N/A'}
					</p>
				</div>
			),
		},
		{
			id: 'actions',
			name: 'Actions',
			sortable: false,
			reorder: true,
			cell: row => (
				<ToolTipButton
					variant="primary"
					iconType="create"
					size="iconButton"
					text="Assign To Customer"
					handleSubmit={() => {
						setSelectedDeviceData([]);
						setSelectedSystemSerialList([]);
						setShowAllDevices(false);
						assignCustomerPrefix(row);
					}}
				/>
			),
		},
	];

	const handleSelectedRowsChange = ({ selectedRows }) => {
		const systemSerialList = selectedRows.map(row => row.system_serial);
		setSelectedSystemSerialList(systemSerialList);
		setSelectedDeviceData(selectedRows);
		setSelectedDeviceNames(selectedRows.map(row => row.machine_name));
	};

	return (
		<Card>
			<CardBody>
				{/* Filter Section */}
				<GpuOneFilterSection
					filters={filters}
					onFilterChange={handleFilterChange}
					onSortChange={handleSortChange}
					onClearFilters={handleClearFilters}
				/>
				{selectedSystemSerialList?.length > 0 && (
					<div
						style={{
							display: 'flex',
							justifyContent: 'flex-end',
							marginBottom: '10px',
						}}
					>
						<Button variant="primary" onClick={assignCustomerPrefix}>
							Assign Customer
						</Button>
					</div>
				)}
				<TableSkeleton
					data-testid="loading-wrap"
					isLoading={isLoading}
					rowsCount={8}
					columnsCount={4}
					showPagination={true}
					headerCellWidth="150px"
					rowCellWidth="130px"
				>
					{machines.length > 0 ? (
						<div className="unassignedMachinesTable">
							<ReactDataTableComponent
								columns={columns}
								data={machines}
								pagination
								progressPending={isLoading}
								responsive={true}
								highlightOnHover
								paginationTotalRows={total}
								paginationServer
								onChangePage={handlePageChange}
								paginationPerPage={perPage}
								onChangeRowsPerPage={handlePerRowsChange}
								paginationRowsPerPageOptions={[10, 20, 30, 40, 100]}
								paginationDefaultPage={currentPage}
								persistTableHead
								sortServer
								onSort={handleSort}
								defaultSortFieldId={sortField}
								defaultSortAsc={sortAsc}
								selectableRows
								onSelectedRowsChange={handleSelectedRowsChange}
							/>
						</div>
					) : (
						<p style={{ textAlign: 'center', fontSize: '16px' }}>
							No data found
						</p>
					)}
				</TableSkeleton>
			</CardBody>
			{assignCustomerServerModal && (
				<StyledModal
					show={assignCustomerServerModal}
					onHide={() => {
						setAssignCustomerServerModal(false);
						setSelectedDeviceData([]);
						setSelectedSystemSerialList([]);
						setShowAllDevices(false);
					}}
					color="primary"
				>
					<ModalHeader className="mb-1">
						<ModalCloseButton
							className="lnr lnr-cross"
							aria-label="close-btn"
							type="button"
							onClick={() => setAssignCustomerServerModal(false)}
						/>
						<ModalTitle>Assign Machine to Customer</ModalTitle>
					</ModalHeader>
					{selectedDeviceData?.length > 0 ? (
						<div>
							<div
								style={{
									maxHeight: showAllDevices ? '300px' : 'auto',
									overflowY: showAllDevices ? 'auto' : 'visible',
									border: showAllDevices ? '1px solid #ddd' : 'none',
									borderRadius: showAllDevices ? '4px' : '0',
									padding: showAllDevices ? '10px' : '0',
								}}
							>
								{selectedDeviceData
									.slice(0, showAllDevices ? selectedDeviceData.length : 2)
									.map(device => (
										<div key={device.system_serial}>
											<p style={{ margin: '5px 0' }}>
												Machine Name: <b>{device?.machine_name}</b>
												<br />
												System Serial: <b>{device?.system_serial}</b>
											</p>
											<br />
										</div>
									))}
							</div>
							{selectedDeviceData.length > 2 && (
								<div style={{ marginTop: '10px', textAlign: 'center' }}>
									<Button
										variant="primary"
										size="sm"
										onClick={() => setShowAllDevices(!showAllDevices)}
									>
										{showAllDevices
											? 'See Less'
											: `See More (${selectedDeviceData.length - 2} more)`}
									</Button>
								</div>
							)}
						</div>
					) : (
						<div>
							<p>
								Machine Name: <b>{rowData?.machine_name}</b>
							</p>
							<p>
								System Serial: <b>{rowData?.system_serial}</b>
							</p>
						</div>
					)}
					<div style={{ marginTop: '30px' }}>
						<SelectField
							name="customer"
							label="Select Customer *"
							options={CustomerOptions}
							placeholder="Select Customer"
							value={selectedCustomer}
							onChange={setSelectedCustomer}
						/>
					</div>
					<div style={{ marginTop: '20px', textAlign: 'center' }}>
						<Button
							variant="primary"
							size="sm"
							onClick={
								selectedDeviceData?.length > 0
									? handleAssignMachineToMultiple
									: handleAssignMachine
							}
							disabled={!selectedCustomer}
						>
							Assign Machine
						</Button>
					</div>
				</StyledModal>
			)}
			{viewDetailsModal && (
				<Suspense fallback={<div>Loading...</div>}>
					<ViewDetailsModal
						modal={viewDetailsModal}
						setModal={setViewDetailsModal}
						toggle={() => setViewDetailsModal(false)}
						rowData={rowData}
						fetchData={fetchData}
					/>
				</Suspense>
			)}
		</Card>
	);
};

export default GpuOneUnAssignedMachinesTable;
